import Phaser from 'phaser';
import { saveStorage } from '../saveStorage';
import { ProgressService } from '../../services/progressService';
import { LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS } from '../../config/levels';
import { availableTowers } from '../../config/unlocks';
import { getAudioService } from '../audio/AudioService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';
import { drawStarfield } from '../starfield';
import { LEADERBOARD_GREEN_HEX } from '../chapterTabs';
import { ScrollList } from '../../ui/scrollList';
import { AVATAR_ICONS, avatarUnlocked, type AvatarIcon } from '../../config/avatars';
import { drawAvatarIcon } from '../avatarIcon';
import { fetchMyAvatar, saveAvatar } from '../../account/leaderboardApi';

/**
 * AvatarScene — pick the icon other players see beside your name.
 *
 * Only in-game art: the towers on your roster, the two currencies, a handful of
 * enemies and whatever you bought in the Avatar Shop. There is no upload and no
 * free text, which is the whole reason an avatar is allowed here at all (spec
 * §9). A tower you have not unlocked is shown dimmed and refuses the tap — and
 * the database refuses it too, so the rule is true of the data and not just of
 * this menu.
 *
 * Shop icons behave differently from towers: an unbought one is not in the grid
 * at all (there is a shop to see it in), and a bought one carries a yellow star
 * so it reads as something earned rather than unlocked.
 *
 * Specs: docs/superpowers/specs/2026-09-20-leaderboards-design.md §2,
 *        docs/superpowers/specs/2026-09-21-avatar-shop-design.md §1
 */

const GRID_X = 240;
const GRID_W = 800;
const GRID_TOP = 210;
const GRID_H = 430;
const COLS = 8;
const CELL = GRID_W / COLS; // 100
const CELL_PITCH_Y = 100;
const ICON_SIZE = 62;
/** Caption line, between the subtitle and the grid. */
const CAPTION_Y = 168;

export class AvatarScene extends Phaser.Scene {
  private progress!: ProgressService;
  private list?: ScrollList;
  private roster: ReturnType<typeof availableTowers> = [];
  /** Avatar Shop codes this player has bought — what the grid shows extra. */
  private ownedIcons: string[] = [];
  /** Base icons plus the bought shop ones, in picker order. */
  private visible: AvatarIcon[] = [];
  private equipped = '';
  private selectionBox?: Phaser.GameObjects.Graphics;
  private caption?: Phaser.GameObjects.Text;
  private notice?: Phaser.GameObjects.Text;
  private cellAt = new Map<string, { x: number; y: number }>();
  /** Blocks a second tap while an equip is in flight. */
  private saving = false;

  constructor() {
    super({ key: 'AvatarScene' });
  }

  create(): void {
    this.progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
    this.roster = availableTowers(this.progress.enteredLevels(), this.progress.unlockedExtras());
    this.ownedIcons = this.progress.ownedAvatarIcons();
    // An unbought shop icon is not in the grid: the Avatar Shop is where you
    // see it, and a locked cell here would just be an advert with no price.
    this.visible = AVATAR_ICONS.filter(
      (i) => i.group !== 'shop' || this.ownedIcons.includes(i.code!),
    );
    this.cellAt = new Map();
    this.equipped = '';
    this.saving = false;

    drawStarfield(this, 1280, 720);

    this.add
      .text(640, 48, 'EDIT AVATAR', {
        fontSize: '38px',
        color: LEADERBOARD_GREEN_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.add
      .text(640, 84, 'PICK THE ICON OTHER PLAYERS SEE BESIDE YOUR NAME', {
        fontSize: '15px',
        color: '#9aa6ad',
      })
      .setOrigin(0.5);

    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerup', () => this.scene.start('LeaderboardScene'));

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    // One caption line instead of a label under every icon: forty-seven long
    // tower names in a grid would overlap on CI's wider fonts.
    this.caption = this.add
      .text(640, CAPTION_Y, '', { fontSize: '18px', color: '#e8ecef', fontStyle: 'bold' })
      .setOrigin(0.5);
    this.notice = this.add
      .text(640, 680, '', { fontSize: '15px', color: '#e8a0a0', fontStyle: 'italic' })
      .setOrigin(0.5);

    this.list = new ScrollList(this, {
      x: GRID_X,
      y: GRID_TOP,
      width: GRID_W,
      height: GRID_H,
      barX: GRID_X + GRID_W + 18,
    });

    this.buildGrid();

    void fetchMyAvatar().then((key) => {
      if (!this.scene.isActive()) return;
      this.equipped = key;
      this.drawSelection();
    });

    installDebugReturn(this); // Esc → DemoScene when launched via ?debug
  }

  private buildGrid(): void {
    if (!this.list) return;
    this.visible.forEach((icon, i) => {
      const col = i % COLS;
      const rowIdx = Math.floor(i / COLS);
      const cx = col * CELL + CELL / 2;
      const cy = rowIdx * CELL_PITCH_Y + CELL_PITCH_Y / 2;
      this.cellAt.set(icon.key, { x: cx, y: cy });
      this.list!.content.add(this.buildCell(icon, cx, cy));
    });
    const rows = Math.ceil(this.visible.length / COLS);
    this.list.setContentHeight(rows * CELL_PITCH_Y);

    this.selectionBox = this.add.graphics().setDepth(0);
    this.list.content.add(this.selectionBox);
    this.drawSelection();
  }

  private buildCell(icon: AvatarIcon, cx: number, cy: number): Phaser.GameObjects.Container {
    const c = this.add.container(cx, cy);
    const unlocked = avatarUnlocked(icon, this.roster, this.ownedIcons);

    const plate = this.add.graphics();
    plate.fillStyle(0x1b2430, 0.9);
    plate.fillRoundedRect(-CELL / 2 + 6, -CELL_PITCH_Y / 2 + 6, CELL - 12, CELL_PITCH_Y - 12, 10);
    plate.lineStyle(1, 0x2f3d4c, 1);
    plate.strokeRoundedRect(-CELL / 2 + 6, -CELL_PITCH_Y / 2 + 6, CELL - 12, CELL_PITCH_Y - 12, 10);
    c.add(plate);

    // A shop icon's star sits under it, so its art gets a slightly shorter box
    // to keep the two from touching.
    const shop = icon.group === 'shop';
    const img = drawAvatarIcon(this, icon.key, ICON_SIZE, shop ? ICON_SIZE - 14 : ICON_SIZE);
    if (img) {
      if (shop) img.setY(-8);
      if (!unlocked) img.setAlpha(0.28).setTint(0x8899aa);
      c.add(img);
    }

    if (shop) {
      c.add(
        this.add
          .text(0, CELL_PITCH_Y / 2 - 20, '★', { fontSize: '18px', color: '#f5c518' })
          .setOrigin(0.5)
          .setName(`avatar-star-${icon.code}`),
      );
    }

    const hit = this.add
      .rectangle(0, 0, CELL - 12, CELL_PITCH_Y - 12, 0x000000, 0)
      .setName(`avatar-cell-${icon.key}`)
      .setInteractive({ useHandCursor: unlocked });
    hit.on('pointerover', () => this.caption?.setText(icon.label));
    hit.on('pointerup', () => this.onPick(icon, unlocked));
    c.add(hit);

    return c;
  }

  private onPick(icon: AvatarIcon, unlocked: boolean): void {
    // The end of a scroll flick is not a tap.
    if (this.list?.dragged) return;
    this.caption?.setText(icon.label);
    if (!unlocked) {
      this.flash(`Unlock ${icon.label} to wear it`);
      return;
    }
    if (this.saving || icon.key === this.equipped) return;

    // Optimistic: the green box moves at once, and steps back if the server
    // refuses, so the picker never claims an icon other players will not see.
    const previous = this.equipped;
    this.equipped = icon.key;
    this.drawSelection();
    this.saving = true;
    void saveAvatar(icon.key).then((ok) => {
      this.saving = false;
      if (!this.scene.isActive()) return;
      if (ok) {
        this.flash('');
        return;
      }
      this.equipped = previous;
      this.drawSelection();
      this.flash('Could not save that icon — try again');
    });
  }

  /** The green box marking the equipped icon. */
  private drawSelection(): void {
    const g = this.selectionBox;
    if (!g) return;
    g.clear();
    const cell = this.cellAt.get(this.equipped);
    if (!cell) return;
    g.lineStyle(4, 0x6ecb3c, 1);
    g.strokeRoundedRect(
      cell.x - CELL / 2 + 5,
      cell.y - CELL_PITCH_Y / 2 + 5,
      CELL - 10,
      CELL_PITCH_Y - 10,
      11,
    );
    const icon = this.visible.find((i) => i.key === this.equipped);
    if (icon) this.caption?.setText(`EQUIPPED · ${icon.label}`);
  }

  private flash(msg: string): void {
    this.notice?.setText(msg);
  }
}
